<script lang="ts">
  import { onMount } from "svelte";
  import { deviceDetails } from "./stores/deviceDetails";
  import { page } from "$app/stores";

  // Props
  export let videoMobile: { filename: string };
  export let videoDesktop: { filename: string };
  export let videoPlaceholderMobile: { filename: string; alt: string };
  export let videoPlaceholderDesktop: { filename: string; alt: string };
  export let autoplayOnLoad = false;
  export let restartOnScroll = false;

  // State
  let isMounted = false;
  let videoIsVisible = false;
  let currentVideoSrc = "";

  // Element references
  let video: HTMLVideoElement;
  let videoWrapper: HTMLDivElement;

  // Get the device details
  $: ({ isMobile } = $deviceDetails);

  // Get the video source
  $: videoSrc = isMobile ? videoMobile.filename : videoDesktop.filename;

  // Get the placeholder source
  $: placeholderSrc = isMobile ? videoPlaceholderMobile.filename : videoPlaceholderDesktop.filename;

  // Get the placeholder alt text
  $: placeholderAlt = isMobile ? videoPlaceholderMobile.alt : videoPlaceholderDesktop.alt;

  // Reload and play the video if the source changes (due to browser resizing, etc.)
  $: if (video && videoSrc && videoIsVisible && currentVideoSrc !== videoSrc) {
    video.load();
    setTimeout(() => {
      if (videoIsVisible) {
        video.play();
      }
    }, 50);
  }

  // Prevent videos from loading (for debugging purposes)
  $: noVideo = $page.url.searchParams.get("novideo") === "1";

  // Mount
  onMount(() => {
    isMounted = true;
    currentVideoSrc = videoSrc;

    // Check if the video is visible in the viewport
    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      videoIsVisible = entry.isIntersecting;

      // Toggle video playback (for videos that don't autoplay on load)
      if (video && !autoplayOnLoad) {
        if (videoIsVisible && restartOnScroll) {
          video.currentTime = 0;
          video.play();
        } else if (videoIsVisible) {
          video.play();
        } else {
          video.pause();
        }
      }
    });

    if (videoWrapper) {
      observer.observe(videoWrapper);
    }

    // Unmount
    return () => {
      if (videoWrapper) {
        observer.unobserve(videoWrapper);
      }
    };
  });
</script>

<div class="autoplay-video-wrapper" bind:this={videoWrapper}>
  {#if isMounted && !noVideo}
    <video
      class="autoplay-video"
      src={videoSrc}
      poster={placeholderSrc}
      muted
      loop
      playsinline
      autoplay={autoplayOnLoad}
      bind:this={video}
    />
  {:else if placeholderSrc}
    <img class="autoplay-video-placeholder" src={placeholderSrc} alt={placeholderAlt} />
  {/if}
</div>

<style lang="postcss">
  .autoplay-video,
  .autoplay-video-placeholder {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
</style>
